import Button from 'pf9-ui-components/built/elements/button/Button'
import Modal from 'pf9-ui-components/built/elements/modal/Modal'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import Alert from 'core/components/Alert'
import { getDeleteConfirmText } from 'core/components/CRUDListContainer'
import useListAction from 'core/hooks/useListAction'
import useUpdateAction from 'core/hooks/useUpdateAction'
import useScopedPreferences from 'core/session/useScopedPreferences'
import { deleteKaapiMachines, updateKaapiMachine } from 'k8s/components/kaapi/machines/actions'
import { listKaapiClusters, updateNodePools } from '../kaapi/clusters/actions'
import { kaapiClustersOverviewSelector } from '../kaapi/clusters/selectors'
import { updateKaapiMachineDeployment } from '../kaapi/machine-deployment/actions'
import { LAST_RUNNING_WORKER_NODE_DISACLAIMER } from './constant'

const DELETE_BYO_WORKER_NODE_LABEL = 'cluster.x-k8s.io/delete-machine'

// Helper function to get the delete text
export const getDeleteText = (
  deleteConfirmText: JSX.Element | string,
  isLastRunningNode: boolean,
): JSX.Element | string => {
  if (isLastRunningNode) {
    return (
      <>
        <strong>{LAST_RUNNING_WORKER_NODE_DISACLAIMER}</strong>
        <br />
        <br />
        {deleteConfirmText}
      </>
    )
  }
  return <>{deleteConfirmText}</>
}

const DeleteWorkerNodesDialog = ({
  rows,
  onClose,
  isLastRunningNode,
  reloadKaapiMachines,
  isBYOHCluster = false,
}) => {
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(false)
  const { prefs: k8sPluginGlobalPerfs } = useScopedPreferences('k8sPluginGlobalParams')

  // Load the list of clusters
  useListAction(listKaapiClusters)
  const clusters = useSelector(kaapiClustersOverviewSelector)
  const cluster = clusters?.find(
    (cluster) => cluster.metadata.name === k8sPluginGlobalPerfs?.cluster,
  )

  const {
    update: deletingMachinesFn,
    updating: deletingMachines,
    error: deletingMachinesError,
  } = useUpdateAction(deleteKaapiMachines)

  const {
    update: updatingMachinesFn,
    updating: updatingMachines,
    error: updatingMachinesError,
  } = useUpdateAction(updateKaapiMachine)
  const {
    update: updateMachineDeployment,
    updating: updatingMachineDeployment,
    error: updateMachineDeploymentError,
  } = useUpdateAction(updateKaapiMachineDeployment)
  const {
    update: updateNodePool,
    updating: updatingNodePools,
    error: updateNodePoolsError,
  } = useUpdateAction(updateNodePools)

  const deleteConfirmText = useMemo(() => {
    const deleteConfirmText = getDeleteConfirmText(rows.flatMap((node) => node.metadata))
    return getDeleteText(deleteConfirmText, isLastRunningNode)
  }, [rows, isLastRunningNode])

  // Handle virtualized node deletion
  const deleteVirtualizedNodes = useCallback(async () => {
    setLoading(true)
    const promises = []
    for (const node of rows) {
      if (!node?.kind) return
      promises.push(
        deletingMachinesFn({
          name: node.metadata.name,
          namespace: node.metadata.namespace,
        }),
      )
    }

    await Promise.all(promises)
      .then(() => {
        reloadKaapiMachines(true)
        onClose(true)
      })
      .catch((err) => {
        console.error(err)
        setError({ title: 'Error Deleting Node Group', message: err?.message })
      })
      .finally(() => {
        setLoading(false)
      })
  }, [rows])

  // Handle BYOH cluster node deletion
  const deleteByoNodes = useCallback(async () => {
    setLoading(true)
    const uniqueMachineDeployments = new Map<string, number>([])
    const uniqueMachineDeploymentsLabels = new Map<string, string>([])
    const promisesToUpdateNodes = []
    const promisesToUpdateMachineDeployments = []

    // Update the selected nodes
    for (const node of rows) {
      promisesToUpdateNodes.push(
        updatingMachinesFn({
          name: node.metadata.name,
          namespace: node.metadata.namespace,
          customBody: {
            metadata: {
              annotations: {
                [DELETE_BYO_WORKER_NODE_LABEL]: 'yes',
              },
            },
          },
          contentType: 'application/merge-patch+json',
        }),
      )

      // If the machine deployment is already in the map, update the replicas
      if (uniqueMachineDeployments.has(node.machineDeployment.metadata.name)) {
        const currentReplicaCount = uniqueMachineDeployments.get(
          node.machineDeployment.metadata.name,
        )
        uniqueMachineDeployments.set(node.machineDeployment.metadata.name, currentReplicaCount - 1)
        uniqueMachineDeploymentsLabels.set(
          node.machineDeployment.metadata.name,
          node?.metadata?.labels?.['topology.cluster.x-k8s.io/deployment-name'],
        )
      } else {
        uniqueMachineDeployments.set(
          node.machineDeployment.metadata.name,
          node.machineDeployment.spec?.replicas - 1,
        )
        uniqueMachineDeploymentsLabels.set(
          node.machineDeployment.metadata.name,
          node?.metadata?.labels?.['topology.cluster.x-k8s.io/deployment-name'],
        )
      }
    }

    const promisesToUpdateNodesRes = await Promise.all(promisesToUpdateNodes)
    if (promisesToUpdateNodesRes.some((r) => !r.success)) {
      setError({ message: 'Error Deleting Node Group' })
      setLoading(false)
      return
    }

    // Update the machine deployments replicas
    for (const [machineDeploymentName, replicaCount] of uniqueMachineDeployments) {
      if (cluster?.spec?.topology) {
        const selectedNodeIndex =
          cluster?.spec?.topology?.workers?.machineDeployments?.findIndex(
            (md) => md?.name === uniqueMachineDeploymentsLabels.get(machineDeploymentName),
          ) ?? 0
        promisesToUpdateMachineDeployments.push(
          updateNodePool({
            namespace: rows[0].machineDeployment.metadata.namespace,
            body: [
              {
                op: 'replace',
                path: `/spec/topology/workers/machineDeployments/${selectedNodeIndex}/replicas`,
                value: replicaCount < 0 ? 0 : replicaCount,
              },
            ],
            clusterName: cluster?.metadata?.name,
          }),
        )
        continue
      }

      // Remove code after cluster migration
      promisesToUpdateMachineDeployments.push(
        updateMachineDeployment({
          namespace: rows[0].machineDeployment.metadata.namespace, // Only one machine deployment is expected for a BYO cluster
          name: machineDeploymentName,
          customBody: [
            {
              op: 'replace',
              path: '/spec/replicas',
              value: replicaCount < 0 ? 0 : replicaCount,
            },
          ],
        }),
      )
    }

    const promisesToUpdateMachineDeploymentsRes = await Promise.all(
      promisesToUpdateMachineDeployments,
    )
    if (promisesToUpdateMachineDeploymentsRes.some((r) => !r.success)) {
      setError({ message: 'Error Deleting Node Group' })
      setLoading(false)
      return
    }

    setLoading(false)
    reloadKaapiMachines(true)
    onClose(true)
  }, [rows])

  // Set error if any of the update actions fail
  useEffect(() => {
    if (deletingMachinesError || updatingMachinesError || updateMachineDeploymentError) {
      setError(deletingMachinesError || updatingMachinesError || updateMachineDeploymentError)
    }
  }, [deletingMachinesError, updatingMachinesError, updateMachineDeploymentError])

  const submitting = deletingMachines || updatingMachines || updatingMachineDeployment

  return (
    <Modal
      open
      panel="dialog"
      onClose={onClose}
      title="Delete Worker Nodes"
      maxWidth={600}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={isBYOHCluster ? deleteByoNodes : deleteVirtualizedNodes}
            loading={submitting}
          >
            Confirm
          </Button>
        </>
      }
      data-testid="delete-worker-nodes-dialog"
    >
      {error && <Alert variant="error" title={error?.title} message={error?.message} />}
      {deleteConfirmText}
    </Modal>
  )
}

export default DeleteWorkerNodesDialog
